import React from 'react';
import { paletteColors } from '../config/constants';

interface SettingsOverlayProps {
    onClose: () => void;
    translation: 'ERV' | 'KJV';
    handleTranslationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    selectedPalette: string;
    handlePaletteChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    bookGroupType: 'sequence' | 'sections';
    handleBookGroupTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    bookTitleCaps: boolean;
    handleBookTitleCapsChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    bookBorderStyle: 'curved' | 'edged' | 'none';
    handleBookBorderStyleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    settingsOverlayRef: React.RefObject<HTMLDivElement | null>;
    activePage: 'cosmetic' | 'function';
    setActivePage: (page: 'cosmetic' | 'function') => void;
    maxColumns: number;
    handleMaxColumnsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    verseThreshold: number;
    handleVerseThresholdChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    maxVerses: number;
    handleMaxVersesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    maxFontSize: number;
    handleMaxFontSizeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SettingsOverlay: React.FC<SettingsOverlayProps> = ({
    onClose,
    translation,
    handleTranslationChange,
    selectedPalette,
    handlePaletteChange,
    bookGroupType,
    handleBookGroupTypeChange,
    bookTitleCaps,
    handleBookTitleCapsChange,
    bookBorderStyle,
    handleBookBorderStyleChange,
    settingsOverlayRef,
    activePage,
    setActivePage,
    maxColumns,
    handleMaxColumnsChange,
    verseThreshold,
    handleVerseThresholdChange,
    maxVerses,
    handleMaxVersesChange,
    maxFontSize,
    handleMaxFontSizeChange
}) => {
    return (
        <div className="settings-overlay" ref={settingsOverlayRef}>
            <div className="settings-overlay-header">
                <div className="settings-tabs">
                    <button
                        className={`settings-tab-button ${activePage === 'cosmetic' ? 'active' : ''}`}
                        onClick={() => setActivePage('cosmetic')}
                    >
                        Cosmetic
                    </button>
                    <button
                        className={`settings-tab-button ${activePage === 'function' ? 'active' : ''}`}
                        onClick={() => setActivePage('function')}
                    >
                        Function
                    </button>
                </div>
                <button onClick={onClose} className="close-settings-button">Close</button>
            </div>

            {activePage === 'cosmetic' && (
                <div className="settings-page">
                    <div className="settings-item">
                        <label htmlFor="palette-select">Color Palette:</label>
                        <select id="palette-select" value={selectedPalette} onChange={handlePaletteChange}>
                            {Object.entries(paletteColors).map(([key, palette]) => (
                                <option key={key} value={key}>
                                    {palette.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="settings-item">
                        <label htmlFor="book-group-select">Book Grouping:</label>
                        <select id="book-group-select" value={bookGroupType} onChange={handleBookGroupTypeChange}>
                            <option value="sequence">Sequence</option>
                            <option value="sections">Sections</option>
                        </select>
                    </div>
                    <div className="settings-item">
                        <label htmlFor="book-title-caps-select">Book Titles:</label>
                        <select id="book-title-caps-select" value={bookTitleCaps ? 'uppercase' : 'normal'} onChange={handleBookTitleCapsChange}>
                            <option value="normal">Normal Case</option>
                            <option value="uppercase">UPPERCASE</option>
                        </select>
                    </div>
                    <div className="settings-item">
                        <label htmlFor="book-border-style-select">Book Borders:</label>
                        <select id="book-border-style-select" value={bookBorderStyle} onChange={handleBookBorderStyleChange}>
                            <option value="curved">Curved</option>
                            <option value="edged">Edged</option>
                            <option value="none">None</option>
                        </select>
                    </div>
                </div>
            )}

            {activePage === 'function' && (
                <div className="settings-page">
                    <div className="settings-item">
                        <label htmlFor="translation-select">Translation:</label>
                        <select id="translation-select" value={translation} onChange={handleTranslationChange}>
                            <option value="ERV">(ERV) Easy-to-Read Version</option>
                            <option value="KJV">(KJV) King James Version</option>
                        </select>
                    </div>
                    <div className="settings-item">
                        <label htmlFor="max-columns-slider">Max Columns: {maxColumns}</label>
                        <input
                            type="range"
                            id="max-columns-slider"
                            min="20"
                            max="74"
                            value={maxColumns}
                            onChange={handleMaxColumnsChange}
                            className="settings-slider"
                        />
                    </div>
                    <div className="settings-item">
                        <label htmlFor="verse-threshold-slider">Verse Threshold: {verseThreshold}</label>
                        <input
                            type="range"
                            id="verse-threshold-slider"
                            min="1"
                            max={maxColumns}
                            value={verseThreshold}
                            onChange={handleVerseThresholdChange}
                            className="settings-slider"
                        />
                    </div>
                    <div className="settings-item">
                        <label htmlFor="max-verses-slider">Verse Size Increments: {maxVerses}</label>
                        <input
                            type="range"
                            id="max-verses-slider"
                            min="1"
                            max="10"
                            value={maxVerses}
                            onChange={handleMaxVersesChange}
                            className="settings-slider"
                        />
                    </div>
                    <div className="settings-item">
                        <label htmlFor="max-font-size-input">Max Font Size (px):</label>
                        <input
                            type="number"
                            id="max-font-size-input"
                            min="10"
                            max="100"
                            value={maxFontSize}
                            onChange={handleMaxFontSizeChange}
                            className="settings-input"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsOverlay;
